import { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { uniqueChannelName } from '../../lib/realtime';
import { supabase } from '../../lib/supabase';
import { Switch } from '../ui';

type BreakerRow = {
  account: string;
  is_on: boolean;
  updated_at: string;
};

/**
 * Auto-agree breaker for the inbox. Deliberately shaped like
 * QueueBreakerToggle, because two breakers that look different would be a trap.
 *
 * One difference on purpose: this one says out loud what it gives up. The queue
 * breaker decides whether Owlbert takes work; this decides whether a human
 * reads their mail before it is acted on, so ON gets a visible warning rather
 * than a quiet green state.
 *
 * A second difference, added 2026-09-15: this breaker governs one mailbox, the
 * signed-in user's own. It used to be a single shared row, so one person
 * flipping it changed how everyone else's mail was handled without telling
 * them. RLS enforces the scoping; the `account` filters here just keep the UI
 * honest about which row it is showing.
 */
const HINT =
  'Your mailbox only. On: each pass agrees to its own analysis and carries it out, with no review. Trash is never automatic and replies still only become drafts — sending stays a click in the drawer.';

export default function EmailBreakerToggle() {
  const { user } = useUser();
  const [isOn, setIsOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const account = user?.email ?? null;

  const load = async () => {
    if (!account) return;
    const { data, error: err } = await supabase
      .from('email_breaker')
      .select('account, is_on, updated_at')
      .eq('account', account)
      .maybeSingle();
    if (err) throw err;
    // No row is the honest default for a mailbox nobody has opted in: off.
    setIsOn(data ? Boolean((data as BreakerRow).is_on) : false);
  };

  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading email breaker:', err);
          setError('Breaker unavailable');
        }
      }
    })();
    const channel = supabase
      .channel(uniqueChannelName('email_breaker'))
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_breaker',
          filter: `account=eq.${account}`,
        },
        () => {
          load().catch((e) => console.error('Error refreshing email breaker:', e));
        },
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [account]);

  const handleChange = async (next: boolean) => {
    if (busy || !account) return;
    setBusy(true);
    setError(null);
    const prev = isOn;
    setIsOn(next);
    try {
      // Upsert rather than update: a mailbox that has queue rows but no
      // breaker row would otherwise update nothing and silently snap back.
      const { error: err } = await supabase.from('email_breaker').upsert(
        {
          account,
          is_on: next,
          updated_by: user?.id ?? null,
        },
        { onConflict: 'account' },
      );
      if (err) throw err;
    } catch (err) {
      console.error('Error updating email breaker:', err);
      setIsOn(prev);
      setError('Could not flip the breaker');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      title={HINT}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}
    >
      {error ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--danger-400)' }}>{error}</span>
      ) : null}
      <span className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
        Auto-agree
      </span>
      <Switch
        checked={isOn}
        disabled={busy}
        onChange={handleChange}
        label={
          <span
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              color: isOn ? 'var(--status-warn)' : 'var(--text-muted)',
              minWidth: '24px',
            }}
          >
            {isOn ? 'On' : 'Off'}
          </span>
        }
      />
    </div>
  );
}
